/**
 * Absent measurements versus zero (#3858).
 *
 * The satisfaction and agent reports return `null` for a metric that was not
 * measured and `0` only when the measured value really is 0. Rendering both as
 * "0" makes a missing measurement look like a bad result, so every number the
 * sections show goes through these helpers.
 */

import type {
  AgentPerformanceData,
  AgentPerformanceTrend,
  CustomerSatisfactionData,
  SatisfactionTrend,
} from '@/types';

/** Shown in place of a value that was not measured. Never a number. */
export const NOT_MEASURED_DASH = '—';

/** True for a real, finite number (0 included); false for null, undefined and NaN. */
export function isMeasured(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** `value.toFixed(digits)` plus an optional unit, or the dash when not measured. */
export function formatMeasured(
  value: number | null | undefined,
  digits = 1,
  unit = '',
): string {
  return isMeasured(value) ? `${value.toFixed(digits)}${unit}` : NOT_MEASURED_DASH;
}

/**
 * Chart tooltip text for a point that may be a gap. Recharts passes numbers,
 * numeric strings, arrays or undefined; only a finite number is formatted.
 */
export function formatChartValue(value: unknown, notMeasuredLabel: string, digits = 1): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(digits)
    : notMeasuredLabel;
}

/** True when a trend series has at least one plottable (non-null) point for `key`. */
export function hasPlottablePoint<T extends object>(
  rows: readonly T[],
  keys: readonly (keyof T)[],
): boolean {
  return rows.some((row) =>
    keys.some((key) => {
      const value = row[key];
      return typeof value === 'number' && Number.isFinite(value);
    }),
  );
}

/**
 * The customer satisfaction section has data when anything was rated. Every
 * other figure in the report derives from ratings, so a period without ratings
 * is empty regardless of how many bookings were completed.
 */
export function hasSatisfactionData(
  data: CustomerSatisfactionData,
  trend: readonly SatisfactionTrend[],
): boolean {
  const { excellent, good, average, poor, terrible } = data.distribution;
  return (
    data.totalResponses > 0 ||
    excellent + good + average + poor + terrible > 0 ||
    trend.length > 0
  );
}

/**
 * The agent section has data when ANY source recorded something: inbound
 * messages, LLM turns (a tenant can have LLM turns without inbound messages),
 * handoffs, errors or intents. Judging by `totalInteractions` alone would hide
 * a period that only has LLM or handoff data.
 */
export function hasAgentPerformanceData(
  data: AgentPerformanceData,
  trend: readonly AgentPerformanceTrend[],
): boolean {
  return (
    data.totalInteractions > 0 ||
    data.handoffConversations > 0 ||
    isMeasured(data.avgResponseTime) ||
    isMeasured(data.handoffRate) ||
    isMeasured(data.intentCoverage) ||
    data.agentBreakdown.length > 0 ||
    data.intentDistribution.length > 0 ||
    data.topErrors.length > 0 ||
    trend.length > 0
  );
}

/** Placeholder while a query has no data; every metric is "not measured". */
export const EMPTY_SATISFACTION: CustomerSatisfactionData = {
  overallScore: null,
  nps: null,
  totalResponses: 0,
  responseRate: null,
  avgResponseTime: null,
  satisfactionChange: null,
  distribution: { excellent: 0, good: 0, average: 0, poor: 0, terrible: 0 },
  topComplaints: [],
  topPraises: [],
};

export const EMPTY_AGENT_PERFORMANCE: AgentPerformanceData = {
  totalInteractions: 0,
  successfulResolutions: null,
  avgResponseTime: null,
  avgResolutionTime: null,
  handoffRate: null,
  handoffConversations: 0,
  handoffCoverage: { eligibleConversations: 0, conversationsWithSession: 0 },
  intentAccuracy: null,
  intentCoverage: null,
  performanceChange: null,
  agentBreakdown: [],
  intentDistribution: [],
  topErrors: [],
};
